import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, getToken } from "@/lib/api";
import { t } from "@/i18n";
import {
  Flame,
  CalendarDays,
  LogIn,
  Trophy,
  Type,
  Languages,
  BookOpen,
  AlertTriangle,
  Download,
  CheckCircle2,
  Award,
  ChevronRight,
  ChevronDown,
  X,
  ArrowRight,
} from "lucide-react";

/* Routes de sortie */
const ROUTES = {
  alphabet: "/practice/alphabet",
  vocab: "/practice/vocab",
  lessons: "/roadmap",
  errors: "/my-errors",
  review: "/practice/review",
};

const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

const C = {
  letters: "#7C3AED",
  words: "#0EA5E9",
  lessons: "#16A34A",
  errors: "#DC2626",
  gold: "#CA8A04",
};

const pct = (a, b) => Math.round((100 * (a || 0)) / Math.max(1, b || 0));

/* Les 4 modules de l'alphabet selon ta structure */
import { ALPHABET_MODULES } from "@/lib/alphabetModules";

const MASTERED_BOX = 3; // aligné sur server.py

/* Source unique : alphabetModules.js. `key` = préfixe des clés de maîtrise. */
const ALPHA_MODULES = ALPHABET_MODULES.map((m) => ({
  id: m.slug, // lettres | harakat | formes | ligatures
  title: m.title,
  short: m.tagline.split(" ").slice(0, 2).join(" "),
  total: m.total,
}));

/* Valeurs par défaut : évite tout crash si le back renvoie une réponse partielle
   ou si /progress/detail échoue — l'écran s'affiche quand même. */
const EMPTY_DETAIL = {
  streak: 0,
  days_active: 0,
  logins: 0,
  member_since: "—",
  lessons_done: [],
  lessons_done_count: 0,
  total_lessons: 0,
  modules_validated: 0,
  modules_total: 0,
  errors: [],
  errors_count: 0,
  exams: [],
  words: { mastered: 0, seen: 0 },
};

/* ── Conteneur plein écran ── */
const Screen = ({ children, natural = false }) => (
  <div
    className={`fixed inset-x-0 z-20 bg-background flex flex-col overflow-hidden md:static md:z-auto ${
      natural ? "md:h-auto" : "md:h-[calc(100dvh-7rem)]"
    }`}
    style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
  >
    {children}
  </div>
);

const Bar = ({ value, color, className = "h-2" }) => (
  <div
    className={`${className} w-full rounded-full bg-secondary overflow-hidden`}
  >
    <div
      className="h-full rounded-full transition-all"
      style={{ width: `${Math.min(100, value)}%`, background: color }}
    />
  </div>
);

/* ── Panneau de détail ── */
const Panel = ({ title, count, color, onClose, children, cta, ctaLabel }) => (
  <Screen>
    <div className="shrink-0 px-4 pt-3 pb-3 border-b flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-head text-xl font-bold truncate">{title}</div>
        <div className="text-xs text-muted-foreground mb-1.5">
          {count.label}
        </div>
        <Bar value={count.pct} color={color} className="h-1.5" />
      </div>
      <button
        onClick={onClose}
        className="shrink-0 w-9 h-9 rounded-full border grid place-items-center hover:bg-secondary transition"
      >
        <X size={16} />
      </button>
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">{children}</div>
    {cta && (
      <div className="shrink-0 border-t bg-background/95 backdrop-blur-xl p-3">
        <button
          onClick={cta}
          className="w-full max-w-md mx-auto py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
        >
          {ctaLabel} <ArrowRight size={17} />
        </button>
      </div>
    )}
  </Screen>
);

/* ── Tuile Standard (Mots, Leçons) ── */
const Tile = ({
  icon: I,
  color,
  label,
  value,
  total,
  hint,
  onClick,
  testid,
}) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full min-h-0 overflow-hidden rounded-2xl border bg-card p-3 text-left hover:border-primary transition group flex flex-col"
  >
    <div className="flex items-center gap-2.5 mb-2">
      <span
        className="w-9 h-9 rounded-xl grid place-items-center text-white shrink-0"
        style={{ background: color }}
      >
        <I size={18} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-muted-foreground truncate">
          {label}
        </div>
        <div className="font-head font-extrabold leading-none text-[clamp(1.1rem,5.5vw,1.6rem)]">
          {value}
          <span className="text-base text-muted-foreground font-normal">
            /{total}
          </span>
        </div>
      </div>
      <ChevronRight
        size={17}
        className="text-muted-foreground group-hover:text-primary shrink-0"
      />
    </div>
    <div className="mt-auto">
      <Bar value={pct(value, total)} color={color} className="h-1.5" />
      <div className="text-[10px] text-muted-foreground mt-1 truncate">
        {hint}
      </div>
    </div>
  </button>
);

/* ── Tuile Alphabet (4 barres) ── */
const AlphabetTile = ({ byModule, onClick }) => (
  <button
    onClick={onClick}
    className="col-span-full w-full min-h-0 overflow-hidden rounded-2xl border bg-card p-3 text-left hover:border-primary transition group flex flex-col"
  >
    <div className="flex items-center justify-between mb-3 shrink-0">
      <div className="flex items-center gap-2.5">
        <span
          className="w-9 h-9 rounded-xl grid place-items-center text-white shrink-0"
          style={{ background: C.letters }}
        >
          <Type size={18} />
        </span>
        <div>
          <div className="font-head text-base font-bold leading-tight">
            {t("progress.alphabet")}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {t("progress.modules_d_apprentissage", { count: 4 })}
          </div>
        </div>
      </div>
      <ChevronRight
        size={17}
        className="text-muted-foreground group-hover:text-primary shrink-0"
      />
    </div>

    <div className="grid grid-cols-2 landscape:grid-cols-4 gap-x-3 gap-y-2 min-h-0">
      {ALPHA_MODULES.map((mod) => {
        const n = byModule[mod.id]?.mastered || 0;
        return (
          <div key={mod.id} className="min-w-0">
            <div className="flex justify-between text-[11px] mb-1">
              <span className="font-medium truncate mr-2">{mod.title}</span>
              <span className="text-muted-foreground shrink-0 tabular-nums">
                {n}/{mod.total}
              </span>
            </div>
            <Bar
              value={pct(n, mod.total)}
              color={C.letters}
              className="h-1.5"
            />
          </div>
        );
      })}
    </div>
  </button>
);

const Mini = ({ icon: I, value, label }) => (
  <div className="rounded-xl border bg-card px-2 py-1.5 text-center min-h-0 overflow-hidden flex flex-col items-center justify-center h-full">
    <I size={15} className="mx-auto text-primary mb-1" />
    <div className="font-head text-sm font-bold leading-none">{value}</div>
    <div className="text-[9px] text-muted-foreground leading-tight mt-0.5 truncate w-full">
      {label}
    </div>
  </div>
);

export default function Progress() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [groups, setGroups] = useState([]);
  const [panel, setPanel] = useState(null);
  const [pickedLetter, setPickedLetter] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [wordsCache, setWordsCache] = useState({});
  const [loadingWords, setLoadingWords] = useState(null);
  const [alphaTab, setAlphaTab] = useState("lettres");

  /* ── Fetch des groupes de vocabulaire, réutilisable ──
     Extrait de l'ancien useEffect à deps vides pour pouvoir être rappelé :
     - à l'ouverture du panneau "Mots" (les stats globales ne doivent pas
       rester figées sur l'état au montage de la page) ;
     - au retour de focus sur l'onglet (cf. plus bas). */
  const fetchGroups = useCallback(() => {
    return api
      .get("/progression/vocab-groups")
      .then((r) => setGroups(r.data?.groups || []))
      .catch((e) => {
        console.error("[vocab-groups] failed:", e?.response?.status, e.message);
      });
  }, []);

  useEffect(() => {
    let alive = true;

    api
      .get("/progress/detail")
      .then((r) => {
        if (alive) setD({ ...EMPTY_DETAIL, ...r.data });
      })
      .catch((e) => {
        console.error(
          "[progress/detail] failed:",
          e?.response?.status,
          e?.response?.data || e.message,
        );
        if (alive) {
          setD(EMPTY_DETAIL);
          setDetailFailed(true);
        }
      });

    fetchGroups();

    return () => {
      alive = false;
    };
  }, [fetchGroups]);

  /* Refetch des groupes à chaque ouverture du panneau "Mots" : sinon le
     résumé (X mots acquis sur Y · N/M groupes validés) et la barre par
     groupe restent figés sur l'état capturé au montage du composant. */
  useEffect(() => {
    if (panel === "words") fetchGroups();
  }, [panel, fetchGroups]);

  /* Refetch au retour de focus sur l'onglet : couvre le cas où l'utilisateur
     a maîtrisé des mots dans un autre onglet/session puis revient ici. */
  useEffect(() => {
    const onFocus = () => fetchGroups();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchGroups]);

  const [alphaStats, setAlphaStats] = useState(null);
  const [alphaIndex, setAlphaIndex] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let index = {};
      try {
        const { data } = await api.get("/content/alphabet");
        for (const g of data?.groups || [])
          for (const l of g.letter_details || [])
            index[l.char] = {
              name: l.name,
              translit: l.translit,
              module: "lettres",
            };
      } catch {
        // Ignorer
      }
      for (const m of ALPHABET_MODULES) {
        if (m.remote) continue;
        for (const g of m.groups || [])
          for (const l of g.letter_details || [])
            if (!index[l.char])
              index[l.char] = { name: l.name, module: m.slug };
      }

      let stats = {};
      try {
        const { data } = await api.get("/progression/alphabet");
        for (const l of data?.letters || []) {
          stats[l.char] = {
            seen: l.seen || 0,
            box: l.box || 0,
            mastered: !!l.mastered,
          };
        }
      } catch {
        // Ignorer
      }

      if (!cancelled) {
        setAlphaIndex(index);
        setAlphaStats(stats);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const alphaByModule = useMemo(() => {
    const out = {};
    for (const m of ALPHA_MODULES)
      out[m.id] = { mastered: 0, started: 0, letters: [] };
    for (const [char, meta] of Object.entries(alphaIndex)) {
      const s = alphaStats?.[char] || { seen: 0, box: 0, mastered: false };
      const bucket = out[meta.module];
      if (!bucket) continue;
      if (s.mastered) bucket.mastered += 1;
      if (s.seen > 0) bucket.started += 1;
      bucket.letters.push({ char, ...meta, ...s });
    }
    return out;
  }, [alphaIndex, alphaStats]);

  /* Chargement des mots d'un groupe : recharge systématiquement depuis le
     back à chaque clic (plus de `if (wordsCache[gid]) return;`) pour ne
     jamais réafficher un statut de maîtrise périmé si le groupe a déjà été
     ouvert une fois dans la session. */
  const loadGroupWords = useCallback(
    async (gid) => {
      if (openGroup === gid) return setOpenGroup(null);
      setOpenGroup(gid);
      setLoadingWords(gid);
      try {
        const r = await api.get(
          `/progression/vocab-groups/${encodeURIComponent(gid)}/words`,
        );
        setWordsCache((c) => ({ ...c, [gid]: r.data?.words || [] }));
      } catch {
        setWordsCache((c) => ({ ...c, [gid]: [] }));
      } finally {
        setLoadingWords(null);
      }
    },
    [openGroup],
  );

  const dl = async (id, level) => {
    const res = await fetch(`${API}/exam/attestation/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attestation_${level}.pdf`;
    a.click();
  };

  const vocab = useMemo(() => {
    if (!groups.length)
      return { mastered: d?.words?.mastered || 0, total: d?.words?.seen || 0 };
    return {
      mastered: groups.reduce((n, g) => n + (g.mastered || 0), 0),
      total: groups.reduce((n, g) => n + (g.total || 0), 0),
    };
  }, [groups, d]);

  if (!d)
    return (
      <div className="p-6 text-muted-foreground">
        {t("progress.chargement")}
      </div>
    );

  const close = () => {
    setPanel(null);
    setPickedLetter(null);
  };

  /* ══════════════ Panneau ALPHABET (4 Onglets) ══════════════ */
  if (panel === "letters") {
    const cfg =
      ALPHA_MODULES.find((m) => m.id === alphaTab) || ALPHA_MODULES[0];
    const bucket = alphaByModule[alphaTab] || { mastered: 0, letters: [] };
    const list = [...bucket.letters].sort(
      (a, b) => (b.seen > 0) - (a.seen > 0) || b.box - a.box,
    );

    return (
      <Panel
        title={t("progress.alphabet")}
        color={C.letters}
        count={{
          label: t("progress.elements_acquis_sur", {
            mastered: bucket.mastered,
            total: cfg.total,
          }),
          pct: pct(bucket.mastered, cfg.total),
        }}
        onClose={close}
        cta={() => nav(`${ROUTES.alphabet}/${alphaTab}`)}
        ctaLabel={t("progress.continuer_module", { module: cfg.title })}
      >
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 pb-1">
          {ALPHA_MODULES.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                setAlphaTab(m.id);
                setPickedLetter(null);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                alphaTab === m.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:bg-secondary/80"
              }`}
            >
              {m.short}
              <span className="ml-1.5 opacity-70 tabular-nums">
                {alphaByModule[m.id]?.mastered || 0}/{m.total}
              </span>
            </button>
          ))}
        </div>

        {!alphaStats ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            {t("progress.chargement")}
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-1.5">
            {list.map((l) => (
              <button
                key={l.char}
                onClick={() => setPickedLetter(l)}
                className={`rounded-xl border p-2 text-center transition hover:border-primary ${
                  l.mastered
                    ? "bg-emerald-500/10 border-emerald-500/40"
                    : l.seen > 0
                      ? ""
                      : "opacity-55"
                } ${pickedLetter?.char === l.char ? "border-primary ring-1 ring-primary" : ""}`}
              >
                <div className="arabic text-2xl leading-none mb-1" dir="rtl">
                  {l.char}
                </div>
                <div className="text-[10px] text-muted-foreground truncate mb-1">
                  {l.name}
                </div>
                <Bar
                  value={pct(l.box, MASTERED_BOX)}
                  color={l.mastered ? "#16A34A" : C.letters}
                  className="h-1"
                />
              </button>
            ))}
          </div>
        )}

        {pickedLetter && (
          <div className="sticky bottom-0 mt-4 rounded-2xl border bg-card p-3 flex items-center gap-3 shadow-lg">
            <span className="arabic text-3xl" dir="rtl">
              {pickedLetter.char}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">
                {pickedLetter.name}
                {pickedLetter.translit ? ` · ${pickedLetter.translit}` : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("progress.vu_fois", { count: pickedLetter.seen })} ·{" "}
                {pickedLetter.mastered
                  ? t("progress.acquis")
                  : t("progress.validations_sur", {
                      box: pickedLetter.box,
                      total: MASTERED_BOX,
                    })}
              </div>
            </div>
            <button
              onClick={() => setPickedLetter(null)}
              className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </Panel>
    );
  }

  /* ══════════════ Panneau MOTS ══════════════ */
  if (panel === "words") {
    const current = groups.find((g) => g.status === "current") || groups[0];
    return (
      <Panel
        title={t("progress.mots")}
        color={C.words}
        count={{
          label: t("progress.mots_acquis_recap", {
            mastered: vocab.mastered,
            total: vocab.total,
            val: groups.filter((g) => g.validated).length,
            totalGroups: groups.length,
          }),
          pct: pct(vocab.mastered, vocab.total),
        }}
        onClose={close}
        cta={() =>
          nav(
            current
              ? `${ROUTES.vocab}?group=${encodeURIComponent(current.id)}`
              : ROUTES.vocab,
          )
        }
        ctaLabel={t("progress.continuer_mots")}
      >
        {groups.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            {t("progress.aucun_groupe_vocab")}
          </div>
        ) : (
          <div className="space-y-1.5">
            {groups.map((g) => {
              const open = openGroup === g.id;
              const words = wordsCache[g.id] || [];
              return (
                <div
                  key={g.id}
                  className={`rounded-2xl border overflow-hidden ${g.validated ? "border-emerald-500/40" : ""}`}
                >
                  <button
                    onClick={() => loadGroupWords(g.id)}
                    className="w-full p-3 text-left hover:bg-secondary/40 transition"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-medium text-sm truncate flex-1">
                        {g.title}
                      </span>
                      {g.validated && (
                        <CheckCircle2
                          size={14}
                          className="text-emerald-600 shrink-0"
                        />
                      )}
                      <span className="text-xs font-bold tabular-nums shrink-0">
                        {g.mastered}
                        <span className="text-muted-foreground font-normal">
                          /{g.total}
                        </span>
                      </span>
                      <ChevronDown
                        size={15}
                        className={`shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </div>
                    <Bar
                      value={pct(g.mastered, g.total)}
                      color={g.validated ? "#16A34A" : C.words}
                      className="h-1.5"
                    />
                  </button>
                  {open && (
                    <div className="border-t p-2 space-y-1 max-h-64 overflow-y-auto">
                      {loadingWords === g.id ? (
                        <div className="text-xs text-muted-foreground text-center py-3">
                          {t("progress.chargement")}
                        </div>
                      ) : words.length === 0 ? (
                        <div className="text-xs text-muted-foreground text-center py-3">
                          {t("progress.aucun_mot_dans_groupe")}
                        </div>
                      ) : (
                        words.map((w) => {
                          const ok = w.mastered || (w.correct || 0) >= 3; // fallback si le back n'expose pas `mastered` ici
                          return (
                            <div
                              key={w.id}
                              className={`rounded-xl px-2 py-1.5 ${ok ? "bg-emerald-500/10" : ""}`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className="arabic text-base truncate"
                                  dir="rtl"
                                >
                                  {w.word_ar}
                                </span>
                                <span className="text-xs text-muted-foreground truncate flex-1">
                                  {w.translation_en}
                                </span>
                                {ok ? (
                                  <CheckCircle2
                                    size={13}
                                    className="text-emerald-600 shrink-0"
                                  />
                                ) : (
                                  <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                                    {w.correct || 0}/3
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    );
  }

  /* ══════════════ Panneau LEÇONS ══════════════ */
  if (panel === "lessons") {
    const done = d.lessons_done || [];
    return (
      <Panel
        title={t("progress.lecons")}
        color={C.lessons}
        count={{
          label: t("progress.lecons_acquises_recap", {
            mastered: d.lessons_done_count,
            total: d.total_lessons,
            val: d.modules_validated,
            totalMods: d.modules_total,
          }),
          pct: pct(d.lessons_done_count, d.total_lessons),
        }}
        onClose={close}
        cta={() => nav(ROUTES.lessons)}
        ctaLabel={t("progress.continuer_parcours")}
      >
        {done.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            {t("progress.aucune_lecon_commencee")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-1.5">
            {done.map((l, i) => {
              const ok = l.acquired ?? l.passed;
              return (
                <div
                  key={i}
                  className={`rounded-xl border p-2.5 flex items-center gap-2.5 ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-ocre/40 bg-ocre/5"}`}
                >
                  <span
                    className="w-8 h-8 rounded-lg grid place-items-center text-white shrink-0"
                    style={{ background: ok ? C.lessons : "#D97706" }}
                  >
                    <CheckCircle2 size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {l.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {l.theme} ·{" "}
                      {t("progress.meilleur_score", { score: l.score })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    );
  }

  /* ══════════════ Panneau ERREURS ══════════════ */
  if (panel === "errors") {
    return (
      <Panel
        title={t("progress.mes_erreurs")}
        color={C.errors}
        count={{
          label: t("progress.notions_a_retravailler", {
            count: d.errors_count,
          }),
          pct: d.errors_count ? 100 : 0,
        }}
        onClose={close}
        cta={d.errors_count > 0 ? () => nav(ROUTES.review) : null}
        ctaLabel={t("progress.revisions_du_jour")}
      >
        {d.errors.length === 0 ? (
          <div className="rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />{" "}
            {t("progress.aucune_erreur")}
          </div>
        ) : (
          <div className="space-y-1.5">
            {d.errors.map((e, i) => (
              <div key={i} className="rounded-xl border bg-card p-2.5">
                <div className="text-[11px] text-muted-foreground mb-0.5">
                  {t("progress.encore_n_validations", { count: 3 - e.box })}
                </div>
                <div className="text-sm">{e.item}</div>
                <div className="text-sm text-emerald-600">
                  {t("progress.reponse")}{" "}
                  <span className="arabic" dir="rtl">
                    {e.correct}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  /* ══════════════ Panneau EXAMENS ══════════════ */
  if (panel === "exams") {
    const passed = d.exams.filter((e) => e.passed).length;
    return (
      <Panel
        title={t("progress.examens_blancs")}
        color={C.gold}
        count={{
          label: t("progress.reussis_sur_passes", {
            passed,
            total: d.exams.length,
          }),
          pct: pct(passed, d.exams.length || 1),
        }}
        onClose={close}
      >
        {d.exams.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            {t("progress.aucun_examen_passe")}
          </div>
        ) : (
          <div className="space-y-1.5">
            {d.exams.map((e, i) => (
              <div
                key={i}
                className="rounded-xl border bg-card p-3 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Award
                    size={19}
                    style={{ color: e.passed ? "#16A34A" : C.errors }}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {t("progress.examen_level", {
                        level: e.level,
                        score: e.overall,
                      })}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {e.date} ·{" "}
                      {e.passed ? t("progress.reussi") : t("progress.echoue")}
                    </div>
                  </div>
                </div>
                {e.passed && e.id && (
                  <button
                    onClick={() => dl(e.id, e.level)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-ocre text-white text-xs font-medium"
                  >
                    <Download size={14} /> {t("progress.attestation")}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  /* ══════════════ Écran d'accueil ══════════════ */
  return (
    <Screen natural>
      <div className="shrink-0 px-4 pt-3 pb-2">
        <h1 className="font-head text-2xl font-bold leading-tight">
          {t("progress.mes_progres")}
        </h1>
        {detailFailed && (
          <div className="mt-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {t("progress.indisponible")}
          </div>
        )}
      </div>

      <div className="flex-1 px-4 pb-6 flex flex-col gap-3 overflow-y-auto no-scrollbar">
        <AlphabetTile
          byModule={alphaByModule}
          onClick={() => setPanel("letters")}
        />

        <div className="grid grid-cols-2 gap-3">
          <Tile
            icon={Languages}
            color={C.words}
            label={t("progress.mots_acquis")}
            value={vocab.mastered}
            total={vocab.total}
            hint={t("progress.groupes_valides", {
              val: groups.filter((g) => g.validated).length,
              total: groups.length,
            })}
            onClick={() => setPanel("words")}
          />
          <Tile
            icon={BookOpen}
            color={C.lessons}
            label={t("progress.lecons_acquises")}
            value={d.lessons_done_count}
            total={d.total_lessons}
            hint={t("progress.modules_valides", {
              val: d.modules_validated,
              total: d.modules_total,
            })}
            onClick={() => setPanel("lessons")}
          />
        </div>

        <div className="grid grid-cols-4 gap-2 h-[80px]">
          <Mini
            icon={Flame}
            value={`${d.streak} ${t("progress.jour_abbr")}`}
            label={t("progress.serie")}
          />
          <Mini
            icon={CalendarDays}
            value={d.days_active}
            label={t("progress.jours_actifs")}
          />
          <Mini
            icon={LogIn}
            value={d.logins}
            label={t("progress.connexions")}
          />
          <Mini
            icon={Trophy}
            value={d.member_since || "—"}
            label={t("progress.membre_depuis")}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 mt-1">
          <button
            onClick={() =>
              d.errors_count > 0 ? nav(ROUTES.review) : setPanel("errors")
            }
            className="rounded-2xl border bg-card p-3 flex items-center gap-3 text-left hover:border-primary transition"
          >
            <AlertTriangle size={18} className="text-destructive shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-none truncate mb-1">
                {t("progress.mes_erreurs")}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {t("progress.notions_a_retravailler", {
                  count: d.errors_count,
                })}
              </div>
            </div>
          </button>

          <button
            onClick={() => setPanel("exams")}
            className="rounded-2xl border bg-card p-3 flex items-center gap-3 text-left hover:border-primary transition"
          >
            <Award size={18} className="shrink-0" style={{ color: C.gold }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-none truncate mb-1">
                {t("progress.examens")}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {t("progress.n_passes", { count: d.exams.length })}
              </div>
            </div>
          </button>
        </div>
      </div>
    </Screen>
  );
}
